import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  RefreshCw,
  Plus,
  UserPlus,
  PackagePlus,
  CheckCircle2,
  RotateCcw,
  GitBranch,
} from 'lucide-react';
import { resetDemo, errorText } from '../lib/api';
import { confirmDialog } from './ConfirmDialog';
import type { Campaign, Influencer, Product, Run } from '../../../shared/types';
import { useCollection, useRecord } from '../lib/hooks';
import type { AuthUser } from '../lib/pb';
import CampaignList, { DemoBadge } from './CampaignList';
import NewCampaignForm from './NewCampaignForm';
import AvatarCreator from './AvatarCreator';
import ProductCreator from './ProductCreator';
import ScriptEditor from './ScriptEditor';
import ReviewSection from './ReviewSection';
import VideoPlayer from './VideoPlayer';
import RunTimeline from './RunTimeline';
import StageBadge from './StageBadge';

/**
 * The Video Generator tab: campaign list on the left, the current stage's screen
 * on the right. Everything is live — selecting a campaign and watching it move
 * through the pipeline needs no refreshing.
 */
export default function CampaignWorkspace({ user }: { user: AuthUser }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<'creator' | 'history'>('creator');
  const [creatorOpen, setCreatorOpen] = useState<'avatar' | 'product' | null>(null);
  const [editAvatar, setEditAvatar] = useState<Influencer | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [createdNote, setCreatedNote] = useState<string | null>(null);

  const campaigns = useCollection<Campaign>('campaigns', {
    sort: '-created',
    expand: 'influencer,product',
  });

  const influencers = useCollection<Influencer>('influencers', { sort: 'name' });
  const products = useCollection<Product>('products', { sort: 'name' });

  const {
    record: selected,
    loading: selectedLoading,
    error: selectedError,
  } = useRecord<Campaign>('campaigns', selectedId, 'influencer,product,created_by');

  // Deselect ONLY when the server definitively says the record is gone
  // (deleted, or hidden by an ACL change). Never infer it from the list —
  // the list's refetch lags a fresh creation, and that inference is what
  // bounced people back to the form instead of onto their new campaign.
  useEffect(() => {
    if (selectedId && !selectedLoading && !selected && selectedError) {
      setSelectedId(null);
    }
  }, [selectedId, selected, selectedLoading, selectedError]);

  const activeInfluencers = useMemo(
    () => influencers.items.filter((i) => i.active !== false),
    [influencers.items]
  );
  const activeProducts = useMemo(
    () => products.items.filter((p) => p.active !== false),
    [products.items]
  );

  const loading = campaigns.loading || influencers.loading || products.loading;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-6 text-center">
        <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-natural-card">
          <span className="text-3xl animate-bounce">🙇</span>
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-natural-sage/40 animate-spin-slower" />
        </div>
        <p className="font-serif text-lg font-medium tracking-tight">Loading your workspace…</p>
      </div>
    );
  }

  if (campaigns.error) {
    return (
      <div className="zen-card p-6 text-sm text-red-600">
        <p className="font-semibold mb-1">Could not load campaigns</p>
        <p className="text-xs">{campaigns.error}</p>
        <button onClick={() => void campaigns.reload()} className="zen-btn-ghost mt-4">
          <RefreshCw size={14} />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {creatorOpen === 'avatar' && (
        <AvatarCreator
          userId={user.id}
          onClose={() => setCreatorOpen(null)}
          onCreated={(name) => {
            setCreatorOpen(null);
            setCreatedNote(`Avatar "${name}" created — it's ready to pick in new campaigns.`);
            void influencers.reload();
          }}
        />
      )}
      {editAvatar && (
        <AvatarCreator
          userId={user.id}
          existing={editAvatar}
          onClose={() => setEditAvatar(null)}
          onCreated={(name) => {
            setEditAvatar(null);
            setCreatedNote(`Avatar "${name}" updated.`);
            void influencers.reload();
          }}
        />
      )}
      {creatorOpen === 'product' && (
        <ProductCreator
          userId={user.id}
          onClose={() => setCreatorOpen(null)}
          onCreated={(name) => {
            setCreatorOpen(null);
            setCreatedNote(`Product "${name}" created — it's ready to pick in new campaigns.`);
            void products.reload();
          }}
        />
      )}
      {editProduct && (
        <ProductCreator
          userId={user.id}
          existing={editProduct}
          onClose={() => setEditProduct(null)}
          onCreated={(name) => {
            setEditProduct(null);
            setCreatedNote(`Product "${name}" updated.`);
            void products.reload();
          }}
        />
      )}
      <div className="flex gap-2 p-1 bg-stone-100/80 rounded-xl max-w-sm">
        <button
          onClick={() => setView('creator')}
          className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer text-center ${
            view === 'creator' ? 'bg-white shadow-xs' : 'text-natural-text/60 hover:text-natural-text/90'
          }`}
        >
          Campaign Creator
        </button>
        <button
          onClick={() => setView('history')}
          className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer text-center ${
            view === 'history' ? 'bg-white shadow-xs' : 'text-natural-text/60 hover:text-natural-text/90'
          }`}
        >
          History ({campaigns.items.length})
        </button>
      </div>

      {view === 'history' ? (
        <CampaignList
          variant="full"
          campaigns={campaigns.items}
          selectedId={selectedId}
          onSelect={(id) => {
            setSelectedId(id);
            setView('creator');
          }}
          onRefresh={() => void campaigns.reload()}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
          <div className="zen-card p-5 min-h-[180px] md:min-h-[480px]">
            <button
              onClick={() => setSelectedId(null)}
              className="zen-btn-primary w-full mb-2"
            >
              <Plus size={15} />
              Create new video
            </button>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setCreatorOpen('avatar')}
                className="zen-btn-ghost justify-center text-xs"
                title="Add a new avatar (influencer) with locations"
              >
                <UserPlus size={13} />
                Avatar
              </button>
              <button
                onClick={() => setCreatorOpen('product')}
                className="zen-btn-ghost justify-center text-xs"
                title="Add a new product"
              >
                <PackagePlus size={13} />
                Product
              </button>
            </div>
            {createdNote && (
              <p className="mb-3 text-[11px] text-natural-sage flex items-start gap-1.5">
                <CheckCircle2 size={12} className="shrink-0 mt-0.5" />
                {createdNote}
              </p>
            )}
            <h3 className="font-serif text-sm font-medium mb-3">Campaigns</h3>
            <CampaignList
              variant="sidebar"
              campaigns={campaigns.items}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onRefresh={() => void campaigns.reload()}
            />
          </div>

          <div className="md:col-span-3 space-y-6">
            <AnimatePresence mode="wait">
              {!selectedId ? (
                <motion.div
                  key="new"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <NewCampaignForm
                    influencers={activeInfluencers}
                    products={activeProducts}
                    onCreated={(id) => setSelectedId(id)}
                    onEditInfluencer={setEditAvatar}
                    onEditProduct={setEditProduct}
                    userId={user.id}
                    admin={user.role === 'admin'}
                  />
                </motion.div>
              ) : selected ? (
                <motion.div
                  key={selected.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <CampaignHeader
                    campaign={selected}
                    admin={user.role === 'admin'}
                    onOpenCampaign={setSelectedId}
                  />
                  <RunTimeline campaignId={selected.id} stage={selected.stage} />
                  <StageScreen campaign={selected} user={user} onOpenCampaign={setSelectedId} />
                </motion.div>
              ) : (
                <div className="zen-card p-8 text-center text-xs text-natural-text/50">
                  Loading campaign…
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignHeader({
  campaign,
  admin,
  onOpenCampaign,
}: {
  campaign: Campaign;
  admin: boolean;
  onOpenCampaign: (id: string) => void;
}) {
  const influencer = campaign.expand?.influencer as Influencer | undefined;
  const product = campaign.expand?.product as Product | undefined;
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Demo campaigns are replayed: one click wipes runs, reviews and the final
  // video and returns the campaign to a blank draft. Real campaigns never get
  // this button — their history is the audit trail.
  const reset = async () => {
    if (
      !(await confirmDialog({
        title: 'Reset this demo?',
        message:
          'Its runs, log, review rows and final video links are deleted and the campaign goes back to a blank draft, ready to replay from "Create". The demo assets themselves are untouched.',
        confirmLabel: 'Reset demo',
        danger: true,
      }))
    ) {
      return;
    }
    setResetting(true);
    setResetError(null);
    try {
      await resetDemo(campaign.id);
    } catch (err) {
      setResetError(errorText(err));
    } finally {
      setResetting(false);
    }
  };

  // Live cost of this video: the sum over ALL of the campaign's runs (failed
  // and cancelled runs included — their generations were still paid for). The
  // runs subscription updates the number as each generation task is created.
  const runs = useCollection<Run>('runs', { filter: `campaign = "${campaign.id}"` });
  const cost = runs.items.reduce((sum, r) => sum + (r.stats?.cost_usd || 0), 0);

  // Lineage both ways: a revision links back to the video it fixes, and a
  // finished campaign lists the revisions made from it. Each revision's cost
  // is its own (only its runs count above) — the parent's cost is not
  // re-counted.
  const revisions = useCollection<Campaign>('campaigns', {
    filter: `parent_campaign = "${campaign.id}"`,
    sort: 'seq',
  });

  return (
    <div className="zen-card p-5 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="font-mono text-[10px] text-natural-text/40">#{campaign.seq}</span>
          <h2 className="font-serif text-lg font-medium truncate">
            {product?.name || campaign.title || 'Untitled campaign'}
          </h2>
        </div>
        <p className="text-xs text-natural-text/55">
          {/* v1 fell back to `influencers[0]` when it couldn't match, so history
              routinely credited the wrong person. The relation is real now. */}
          {influencer?.name ?? 'No influencer'} · {campaign.aspect_ratio} ·{' '}
          {campaign.mode === 'custom' ? 'Custom script' : 'AI script'}
        </p>
        {(campaign.parent_campaign || revisions.items.length > 0) && (
          <p className="text-[11px] text-natural-text/55 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <GitBranch size={11} className="text-natural-sage shrink-0" />
            {campaign.parent_campaign && (
              <span>
                Revision of{' '}
                <button
                  onClick={() => onOpenCampaign(campaign.parent_campaign!)}
                  className="font-mono text-natural-sage hover:underline cursor-pointer"
                  title="Open the finished video this revision was made from"
                >
                  #{campaign.parent_seq}
                </button>
              </span>
            )}
            {revisions.items.length > 0 && (
              <span>
                {campaign.parent_campaign ? '· ' : ''}Revisions:{' '}
                {revisions.items.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ', '}
                    <button
                      onClick={() => onOpenCampaign(r.id)}
                      className="font-mono text-natural-sage hover:underline cursor-pointer"
                      title={`Open revision #${r.seq} (${r.stage})`}
                    >
                      #{r.seq}
                    </button>
                  </span>
                ))}
              </span>
            )}
          </p>
        )}
        {resetError && <p className="text-[11px] text-red-600 mt-1">{resetError}</p>}
      </div>
      <div className="flex items-center gap-2">
        {campaign.is_demo && <DemoBadge />}
        {campaign.is_demo && admin && (
          <button
            onClick={() => void reset()}
            disabled={resetting}
            className="zen-btn-ghost py-1.5 px-2.5 text-xs"
            title="Wipe this demo's runs and reviews and start again from a blank draft"
          >
            <RotateCcw size={12} />
            {resetting ? 'Resetting…' : 'Reset demo'}
          </button>
        )}
        {cost > 0 && (
          <span
            className="zen-pill bg-natural-bg text-natural-text/70 font-mono"
            title="Generation cost so far: every finished image and video generation for this campaign, at the model prices set in Admin → Model costs. Generations that failed on the provider's end are not billed; reused library images cost nothing."
          >
            Current cost of video: ${cost.toFixed(2)}
          </span>
        )}
        <StageBadge stage={campaign.stage} />
      </div>
    </div>
  );
}

function StageScreen({
  campaign,
  user,
  onOpenCampaign,
}: {
  campaign: Campaign;
  user: AuthUser;
  onOpenCampaign: (id: string) => void;
}) {
  switch (campaign.stage) {
    case 'draft':
    case 'script_generating':
    case 'script_review':
      return <ScriptEditor campaign={campaign} />;

    case 'images_rendering':
    case 'image_review':
      return <ReviewSection campaign={campaign} kind="image" user={user} />;

    case 'videos_rendering':
    case 'video_review':
      return <ReviewSection campaign={campaign} kind="video" user={user} />;

    default:
      // Assembling, transcribing, captioning, publishing, done, failed,
      // needs_attention, cancelled — all handled by the player, which shows
      // progress or the finished video.
      return <VideoPlayer campaign={campaign} onOpenCampaign={onOpenCampaign} />;
  }
}
